/**
 * /audio-cutter's waveform, as geometry: where a time sits on the strip, which time a finger is
 * on, which handle a tap moves, what a key does to a handle, and the bars to draw for a strip of
 * any width. Pure, so the tests read it directly; the page (`audio-cut.ts`) does the drawing.
 */

import { type Handle, NUDGE_S, type Selection } from '../../../../../src/media/audio-cut-math.ts';

/** The x (CSS pixels from the strip's left edge) of time `t`. */
export function xAtTime(t: number, width: number, duration: number): number {
  if (!(duration > 0) || !(width > 0)) return 0;
  const x = (t / duration) * width;
  return x < 0 ? 0 : x > width ? width : x;
}

/** The time under x, held inside 0 … duration. */
export function timeAtX(x: number, width: number, duration: number): number {
  if (!(duration > 0) || !(width > 0)) return 0;
  const t = (x / width) * duration;
  return t < 0 ? 0 : t > duration ? duration : t;
}

/**
 * The handle a tap on the strip moves: the nearer one, and on a tie the one on the tap's side (a
 * tap before the selection is a new start, after it a new end).
 */
export function nearestHandle(t: number, sel: Selection): Handle {
  const toStart = Math.abs(t - sel.start);
  const toEnd = Math.abs(t - sel.end);
  if (toStart < toEnd) return 'start';
  if (toEnd < toStart) return 'end';
  return t <= sel.start ? 'start' : 'end';
}

/** What a key does to a focused handle: a move by seconds, a jump to an end, or nothing. */
export type KeyMove = { readonly by: number } | { readonly to: 'min' | 'max' } | null;

export function keyMove(key: string): KeyMove {
  switch (key) {
    case 'ArrowLeft':
    case 'ArrowDown':
      return { by: -NUDGE_S };
    case 'ArrowRight':
    case 'ArrowUp':
      return { by: NUDGE_S };
    case 'PageDown':
      return { by: -1 };
    case 'PageUp':
      return { by: 1 };
    case 'Home':
      return { to: 'min' };
    case 'End':
      return { to: 'max' };
    default:
      return null;
  }
}

/**
 * The waveform pooled into `columns` bars: each bar the loudest slice of the time it covers, so a
 * short loud sound never disappears between two bars. `peaks[i]` covers i/peakRate seconds on.
 */
export function poolPeaks(
  peaks: Float32Array,
  peakRate: number,
  duration: number,
  columns: number,
): Float32Array {
  const n = Math.max(0, Math.floor(columns));
  const out = new Float32Array(n);
  if (n === 0 || peaks.length === 0 || !(duration > 0) || !(peakRate > 0)) return out;
  const perColumn = (duration * peakRate) / n;
  for (let c = 0; c < n; c++) {
    const from = Math.floor(c * perColumn);
    const to = Math.max(from + 1, Math.floor((c + 1) * perColumn));
    let loud = 0;
    for (let i = from; i < to && i < peaks.length; i++) {
      const v = peaks[i] ?? 0;
      if (v > loud) loud = v;
    }
    out[c] = loud;
  }
  return out;
}

/**
 * Bars scaled so the loudest fills the strip: a quiet recording still shows its shape. A silent
 * one stays flat.
 */
export function normalise(bars: Float32Array): Float32Array {
  let top = 0;
  for (const v of bars) if (v > top) top = v;
  if (top <= 0) return bars;
  const out = new Float32Array(bars.length);
  for (let i = 0; i < bars.length; i++) out[i] = (bars[i] ?? 0) / top;
  return out;
}
